import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import HomeIcon from '@mui/icons-material/Home';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import tutorialService from '../services/TutorialService';

const SCORE_ROWS = [
  { pos: 1, pts: 5 },
  { pos: 2, pts: 4 },
  { pos: 3, pts: 3 },
  { pos: 4, pts: 2 },
  { pos: 5, pts: 1 },
];

const SECTIONS = [
  { key: 'home', icon: <HomeIcon fontSize="small" /> },
  { key: 'results', icon: <BarChartIcon fontSize="small" /> },
  { key: 'history', icon: <HistoryIcon fontSize="small" /> },
  { key: 'leaderboard', icon: <LeaderboardIcon fontSize="small" /> },
  { key: 'achievements', icon: <EmojiEventsIcon fontSize="small" /> },
  { key: 'profile', icon: <PersonIcon fontSize="small" /> },
];

export default function WelcomeModal({ open, onClose }) {
  const { t } = useTranslation();
  const [doNotShow, setDoNotShow] = useState(false);

  const handleClose = () => {
    if (doNotShow) {
      tutorialService.markTutorialDone();
    }
    onClose();
  };

  const handlePlay = () => {
    tutorialService.markTutorialDone();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
    >
      <DialogTitle sx={{ pb: 0, pt: 3, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={800} color="primary">
          {t('welcome.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('welcome.subtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* How to play */}
        <SectionHeader>{t('welcome.howToPlayTitle')}</SectionHeader>
        <Box component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
          {(t('welcome.howToPlay', { returnObjects: true }) || []).map((step, i) => (
            <Typography key={i} component="li" variant="body2" sx={{ mb: 0.75, color: 'text.primary' }}>
              {step}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Scoring */}
        <SectionHeader>{t('welcome.scoringTitle')}</SectionHeader>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('welcome.scoringDesc')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {SCORE_ROWS.map(({ pos, pts }) => (
            <Box
              key={pos}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'grey.100',
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t(`welcome.scoring.pos${pos}`)}
              </Typography>
              <Chip
                label={`${pts} pts`}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, height: 22 }}
              />
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('welcome.scoringMax')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Sections */}
        <SectionHeader>{t('welcome.sectionsTitle')}</SectionHeader>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {SECTIONS.map(({ key, icon }) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {t(`nav.${key}`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`welcome.sections.${key}`)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3, pt: 1 }}>
        <Button variant="contained" size="large" fullWidth onClick={handlePlay} sx={{ borderRadius: 2, fontWeight: 700 }}>
          {t('welcome.letsPlay')}
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={doNotShow}
              onChange={(e) => setDoNotShow(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {t('welcome.doNotShowAgain')}
            </Typography>
          }
          sx={{ m: 0 }}
        />
      </DialogActions>
    </Dialog>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>
      {children}
    </Typography>
  );
}
